import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import '../styles/ArticleModal.css';

const NEWS_API_BASE = '/admin/news/api/get-articles.php';
const BLOG_API_BASE = '/admin/blogs/api/get-posts.php';
const NEWS_CONTENT_API = '/admin/news/api/get-article-content.php';
const BLOG_CONTENT_API = '/admin/blogs/api/get-post-content.php';

export const ArticleModal = ({ isOpen, onClose, articleSlug, type = 'news' }) => {
  const [article, setArticle] = useState(null);
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && articleSlug) {
      fetchArticle();
    }
  }, [isOpen, articleSlug, type]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const fetchArticle = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const apiBase = type === 'news' ? NEWS_API_BASE : BLOG_API_BASE;
      const contentApi = type === 'news' ? NEWS_CONTENT_API : BLOG_CONTENT_API;
      
      // Fetch metadata
      const res = await fetch(`${apiBase}?slug=${encodeURIComponent(articleSlug)}`);
      if (!res.ok) throw new Error('Failed to fetch article');
      const data = await res.json();
      
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error('Article not found');
      }
      
      const articleData = data[0];
      setArticle(articleData);
      
      // Fetch HTML content separately
      const contentRes = await fetch(`${contentApi}?slug=${encodeURIComponent(articleSlug)}`);
      if (contentRes.ok) {
        const contentData = await contentRes.json(); // Changed from .text() to .json()
        
        if (contentData.success) {
          setContent(contentData.content); // Extract content from JSON response
        } else {
          // Fallback to description/excerpt from metadata
          setContent(articleData.description || articleData.excerpt || '');
        }
      } else {
        // Fallback to content from metadata
        setContent(articleData.description || articleData.excerpt || articleData.content || '');
      }
      
    } catch (err) {
      console.error('Article fetch error:', err);
      setError('Failed to load article: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getShareUrl = () => {
    return `${window.location.origin}/${type}/${articleSlug}`;
  };

  const shareViaEmail = () => {
    const url = getShareUrl();
    const subject = encodeURIComponent(article?.title || 'Check out this article');
    window.location.href = `mailto:?subject=${subject}&body=${encodeURIComponent(url)}`;
  };

  const shareViaWhatsApp = () => {
    const url = getShareUrl();
    const text = encodeURIComponent(`${article?.title} - ${url}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      alert('Link copied to clipboard!');
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = getShareUrl();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Link copied to clipboard!');
    }
  };

  const handleClose = () => {
    onClose();
    navigate(type === 'news' ? '/news' : '/blogs');
  };

  if (!isOpen) return null;

  // Render modal at the end of document.body using a portal
  // This ensures it's the last element in the DOM and appears above everything
  return createPortal(
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={handleClose}>
          <i className="fas fa-times"></i>
        </button>

        {isLoading && (
          <div className="modal-loading">
            <div className="spinner"></div>
            <p>Loading article...</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="modal-error">
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
            <button onClick={fetchArticle} className="btn btn-primary">
              <i className="fas fa-sync-alt"></i> Try Again
            </button>
          </div>
        )}

        {!isLoading && !error && article && (
          <div className="modal-content">
            {/* Mobile close button in header */}
            <div className="modal-mobile-header">
              <button className="modal-mobile-close" onClick={handleClose}>
                <i className="fas fa-arrow-left"></i> Back
              </button>
            </div>
            
            <div className="modal-article-header">
              {article.category && (
                <span className="article-category-badge">
                  <i className="fas fa-folder"></i> {article.category}
                </span>
              )}
              {type === 'blogs' && article.tags && (
                <div className="article-tags">
                  {article.tags.map(tag => (
                    <span key={tag} className="tag">
                      <i className="fas fa-tag"></i> {tag}
                    </span>
                  ))}
                </div>
              )}
              <h1>{article.title}</h1>
              <div className="article-meta-info">
                <span><i className="fas fa-user"></i> {article.author || 'Editorial Team'}</span>
                <span><i className="fas fa-calendar"></i> {formatDate(article.date)}</span>
                {type === 'blogs' && article.read_time && (
                  <span><i className="fas fa-clock"></i> {article.read_time} min read</span>
                )}
              </div>
            </div>

            {article.image && (
              <div className="modal-article-image">
                <img src={article.image} alt={article.title} />
              </div>
            )}

            {(article.description || article.excerpt) && (
              <div className="modal-article-abstract">
                <strong>{article.description || article.excerpt}</strong>
              </div>
            )}

            <div className="modal-article-body">
              <div 
                className="article-content"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>

            <div className="modal-share-section">
              <h4><i className="fas fa-share-alt"></i> Share this article</h4>
              <div className="share-buttons">
                <button onClick={copyLink} className="share-btn copy-btn" title="Copy Link">
                  <i className="fas fa-link"></i>
                  <span>Copy Link</span>
                </button>
                <button onClick={shareViaEmail} className="share-btn email-btn" title="Share via Email">
                  <i className="fas fa-envelope"></i>
                  <span>Email</span>
                </button>
                <button onClick={shareViaWhatsApp} className="share-btn whatsapp-btn" title="Share on WhatsApp">
                  <i className="fab fa-whatsapp"></i>
                  <span>WhatsApp</span>
                </button>
                <a 
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(getShareUrl())}&text=${encodeURIComponent(article.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-btn twitter-btn"
                >
                  <i className="fab fa-twitter"></i>
                  <span>Twitter</span>
                </a>
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-btn facebook-btn"
                >
                  <i className="fab fa-facebook"></i>
                  <span>Facebook</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};